// useNodeAddRelated.js

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Utilities
import { isRelatedTypeAllowed } from 'components/addmenu/addMenuTypeFilters';
import { ADD_MENU_ACTION_KEYS } from 'components/addmenu/buildAddMenuChildren';
import { buildNodeAddMenuChildren } from 'components/addmenu/buildAddMenuChildren';
import { getResolvedFields } from 'utilities/NodeType';
import { listAttachMenuTypeKeys } from 'utilities/nodeTypeRelatedLists';
import { ADD_MODAL_IMMEDIATE_RELATED_TYPE_KEYS } from 'components/addmodal/utilities/constants';
import { isProjectNode } from 'nodetypes/purchaseorderitem/import/purchaseOrderItemsImportUtils';
import {
  isPurchaseOrderItemTypeKey,
  isPurchaseOrderNode,
} from 'nodetypes/purchaseorderitem/purchaseOrderItemKeys';
import {
  PROPOSAL_ITEM_TYPE_KEY,
  PROPOSAL_TYPE_KEY,
} from 'nodetypes/proposal/view/proposalGeneralInfoModel';
import {
  INVOICE_ITEM_TYPE_KEY,
  INVOICE_TYPE_KEY,
} from 'nodetypes/invoice/view/invoiceGeneralInfoModel';

// Components
import AddModal from 'components/addmodal/AddModal';
import ImportModal from 'collection/import/ImportModal';
import AddPurchaseOrderItemFromProductModal from 'nodetypes/purchaseorderitem/fromProduct/AddPurchaseOrderItemFromProductModal';
import AddProposalItemFromProductModal from 'nodetypes/proposal/fromProduct/AddProposalItemFromProductModal';
import AddInvoiceItemFromProductModal from 'nodetypes/invoice/fromProduct/AddInvoiceItemFromProductModal';
import PurchaseOrderItemsImportModal from 'nodetypes/purchaseorderitem/import/PurchaseOrderItemsImportModal';

function normalizeTypeKey(value) {
  return String(value || '').trim().toUpperCase();
}

function isProposalNode(node) {
  return normalizeTypeKey(node?.type) === PROPOSAL_TYPE_KEY;
}

function isProposalItemTypeKey(typeKey) {
  return normalizeTypeKey(typeKey) === PROPOSAL_ITEM_TYPE_KEY;
}

function isInvoiceNode(node) {
  return normalizeTypeKey(node?.type) === INVOICE_TYPE_KEY;
}

function isInvoiceItemTypeKey(typeKey) {
  return normalizeTypeKey(typeKey) === INVOICE_ITEM_TYPE_KEY;
}

/**
 * Shared add-related menu children + modals for the tab "+" button and
 * workspace sections "Add related…" submenu.
 *
 * @param {object} options
 * @param {object|null} options.node
 * @param {Function} [options.onQuickActionSelect]
 * @param {Function} [options.onExistingAttached]
 * @returns {{
 *   relatedTypeKeys: string[]|null,
 *   menuChildren: React.ReactNode[],
 *   modals: React.ReactNode,
 * }}
 */
export default function useNodeAddRelated({
  node,
  onQuickActionSelect,
  onExistingAttached,
} = {}) {
  const { activeBase } = useBase();
  const { getType, getAll } = useNodeTypes();
  const { user } = useUser();

  const [addModalMode, setAddModalMode] = useState(null);
  const [linkTypeKey, setLinkTypeKey] = useState('');
  const [importTypeKey, setImportTypeKey] = useState(null);
  const [poItemsImportOpen, setPoItemsImportOpen] = useState(false);
  const [poItemFromProductOpen, setPoItemFromProductOpen] = useState(false);
  const [proposalItemFromProductOpen, setProposalItemFromProductOpen] = useState(false);
  const [invoiceItemFromProductOpen, setInvoiceItemFromProductOpen] = useState(false);

  const baseId = activeBase?.key || node?.baseKey || null;
  const importTypeDef = importTypeKey ? getType(importTypeKey) : null;
  const importResolvedProperties = importTypeKey
    ? getResolvedFields(importTypeKey, { includeHidden: true, excludeSecured: true })
    : [];
  const showPurchaseOrderItemsImport = isProjectNode(node);

  // Attach / Add menu allowlist (respects showInAttachMenu on related edges).
  // null = no related config (unrestricted); [] = related types exist but all attach-hidden.
  const relatedTypeKeys = useMemo(() => {
    const hostType = getType(node?.type);
    const suggested = hostType?.suggestedChildren;
    if (!Array.isArray(suggested) || suggested.length === 0) return null;
    return listAttachMenuTypeKeys(suggested);
  }, [getType, node?.type]);

  const typeDefs = useMemo(() => getAll() || [], [getAll]);

  const handleLinkExisting = useCallback(() => {
    setLinkTypeKey('');
    setAddModalMode('find');
  }, []);

  const handleImportTypeSelect = useCallback((typeKey) => {
    const normalized = String(typeKey || '').trim();
    if (!normalized) return;
    if (!isRelatedTypeAllowed(relatedTypeKeys, normalized)) return;
    setImportTypeKey(normalized);
  }, [relatedTypeKeys]);

  const handleImportOpenChange = useCallback((open) => {
    if (!open) setImportTypeKey(null);
  }, []);

  const handleImportPurchaseOrderItems = useCallback(() => {
    if (!isProjectNode(node)) return;
    setPoItemsImportOpen(true);
  }, [node]);

  const handleTypeSelect = useCallback((typeKey, options = {}) => {
    const normalized = String(typeKey || '').trim().toUpperCase();

    const mediaKey = normalized === ADD_MENU_ACTION_KEYS.PHOTO
      ? 'CAMERA'
      : normalized === ADD_MENU_ACTION_KEYS.VIDEO
        ? 'RECORDING'
        : normalized === ADD_MENU_ACTION_KEYS.UPLOAD
          ? 'ATTACH'
          : normalized;

    if (!isRelatedTypeAllowed(relatedTypeKeys, mediaKey)
      && !isRelatedTypeAllowed(relatedTypeKeys, normalized)) {
      return;
    }

    if (isPurchaseOrderItemTypeKey(normalized) && isPurchaseOrderNode(node)) {
      setPoItemFromProductOpen(true);
      return;
    }

    if (isProposalItemTypeKey(normalized) && isProposalNode(node)) {
      setProposalItemFromProductOpen(true);
      return;
    }

    if (isInvoiceItemTypeKey(normalized) && isInvoiceNode(node)) {
      setInvoiceItemFromProductOpen(true);
      return;
    }

    if (ADD_MODAL_IMMEDIATE_RELATED_TYPE_KEYS.has(mediaKey)) {
      if (typeof onQuickActionSelect === 'function') {
        onQuickActionSelect(mediaKey);
      }
      return;
    }

    if (typeof onQuickActionSelect === 'function') {
      onQuickActionSelect(typeKey, {
        openInPeek: true,
        initialFields: options?.initialFields || null,
      });
      return;
    }

    setLinkTypeKey(typeKey);
    setAddModalMode('link');
  }, [node, onQuickActionSelect, relatedTypeKeys]);

  const handleCloseAddModal = useCallback(() => {
    setAddModalMode(null);
    setLinkTypeKey('');
  }, []);

  const menuChildren = useMemo(() => buildNodeAddMenuChildren({
    typeDefs,
    relatedTypeKeys,
    onLinkExisting: handleLinkExisting,
    onImportTypeSelect: handleImportTypeSelect,
    showPurchaseOrderItemsImport,
    onImportPurchaseOrderItems: handleImportPurchaseOrderItems,
    onTypeSelect: handleTypeSelect,
  }), [
    handleImportPurchaseOrderItems,
    handleImportTypeSelect,
    handleLinkExisting,
    handleTypeSelect,
    relatedTypeKeys,
    showPurchaseOrderItemsImport,
    typeDefs,
  ]);

  const modals = (
    <>
      <ImportModal
        isOpen={Boolean(importTypeKey)}
        setOpen={handleImportOpenChange}
        user={user}
        typeKey={importTypeKey}
        typeDef={importTypeDef}
        baseKey={baseId}
        resolvedProperties={importResolvedProperties}
        getType={getType}
        anchorNode={node}
      />

      <PurchaseOrderItemsImportModal
        isOpen={poItemsImportOpen}
        setOpen={setPoItemsImportOpen}
        projectNode={node}
        user={user}
        baseKey={baseId}
        onImported={(result) => {
          if (typeof onExistingAttached === 'function' && result) {
            onExistingAttached(result);
          }
        }}
      />

      <AddModal
        node={node}
        allowCreate={true}
        anchorNodeKey={node?.key || null}
        findOnly={addModalMode === 'find'}
        initialRelatedTypeKey={addModalMode === 'link' ? linkTypeKey : null}
        isOpen={addModalMode !== null}
        showInjectedAddTypes={true}
        onClose={handleCloseAddModal}
        onExistingAttached={onExistingAttached}
        onTypeSelect={handleTypeSelect}
      />

      <AddPurchaseOrderItemFromProductModal
        isOpen={poItemFromProductOpen}
        onClose={() => setPoItemFromProductOpen(false)}
        purchaseOrderNode={node}
        baseKey={baseId || ''}
        user={user}
        onCreated={(created) => {
          if (typeof onExistingAttached === 'function' && Array.isArray(created) && created.length) {
            onExistingAttached(created);
          }
        }}
      />

      <AddProposalItemFromProductModal
        isOpen={proposalItemFromProductOpen}
        onClose={() => setProposalItemFromProductOpen(false)}
        proposalNode={node}
        baseKey={baseId || ''}
        user={user}
        onCreated={(created) => {
          if (typeof onExistingAttached === 'function' && Array.isArray(created) && created.length) {
            onExistingAttached(created);
          }
        }}
      />

      <AddInvoiceItemFromProductModal
        isOpen={invoiceItemFromProductOpen}
        onClose={() => setInvoiceItemFromProductOpen(false)}
        invoiceNode={node}
        baseKey={baseId || ''}
        user={user}
        onCreated={(created) => {
          if (typeof onExistingAttached === 'function' && Array.isArray(created) && created.length) {
            onExistingAttached(created);
          }
        }}
      />
    </>
  );

  return {
    relatedTypeKeys,
    menuChildren,
    modals,
    handleTypeSelect,
    handleLinkExisting,
    handleImportTypeSelect,
    handleImportPurchaseOrderItems,
    showPurchaseOrderItemsImport,
  };
}
